/**
 * NPCsViewView — dumb view for the campaign NPC roster.
 *
 * The smart wrapper at ../index.tsx owns the React Query / mutation graph;
 * this view receives the loaded NPC list plus mutation pending flags and
 * emits user actions through callback props. Shared grid machinery (filter,
 * selection, counts, pending-first sort) lives in useWorldEntityGrid, and the
 * toolbar + card grid come from the WorldEntityShell primitives; this file
 * keeps only what's NPC-specific: the role/voice/appearance fields, the two
 * Mantine forms, and the local bulk-busy spinner.
 *
 * Confirm dialogs (`modals.openConfirmModal`) stay in the view: they're
 * Mantine UI concerns, and once the user confirms they trigger the
 * appropriate `onDelete` / `onAcceptAllPending` callback prop.
 */

import { NPC_STATUSES, type TNpcStatus } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { modals } from "@mantine/modals"
import clsx from "clsx"
import { useCallback, useEffect, useState } from "react"
import { z } from "zod"
import type { INpc, INpcCreateBody, INpcEditBody } from "../../../lib/api"
import { speakerInitials } from "../../../lib/speaker-format"
import { VoxStatusPill } from "../../VoxStatusPill"
import { useWorldEntityGrid } from "../../WorldEntityShell/useWorldEntityGrid"
import { WorldEntityGrid } from "../../WorldEntityShell/WorldEntityGrid"
import { WorldEntityToolbar } from "../../WorldEntityShell/WorldEntityToolbar"
import shared from "../../WorldEntityShell/worldEntity.module.css"
import type { IViewProps } from "./interfaces"

interface INpcFormValues {
  name: string
  aliases: string[]
  role: string
  status: TNpcStatus
  description: string
  voiceNotes: string
  appearance: string
}

const npcSchema = z.object({
  name: z.string().min(1, "Name is required"),
  aliases: z.array(z.string()),
  role: z.string(),
  status: z.enum([...NPC_STATUSES] as [TNpcStatus, ...TNpcStatus[]]),
  description: z.string(),
  voiceNotes: z.string(),
  appearance: z.string(),
})

const npcIsPending = (npc: INpc): boolean => npc.pending
const npcName = (npc: INpc): string => npc.name
const npcId = (npc: INpc): string => npc.id

function emptyFormValues(): INpcFormValues {
  return {
    name: "",
    aliases: [],
    role: "",
    status: "alive",
    description: "",
    voiceNotes: "",
    appearance: "",
  }
}

function formValuesFromNpc(npc: INpc): INpcFormValues {
  return {
    name: npc.name,
    aliases: npc.aliases,
    role: npc.role ?? "",
    status: npc.status,
    description: npc.description ?? "",
    voiceNotes: npc.voiceNotes ?? "",
    appearance: npc.appearance ?? "",
  }
}

function buildCreatePayload(v: INpcFormValues): INpcCreateBody {
  const out: INpcCreateBody = { name: v.name.trim(), status: v.status }
  if (v.aliases.length) out.aliases = v.aliases
  if (v.role.trim()) out.role = v.role.trim()
  if (v.description.trim()) out.description = v.description.trim()
  if (v.voiceNotes.trim()) out.voiceNotes = v.voiceNotes.trim()
  if (v.appearance.trim()) out.appearance = v.appearance.trim()
  return out
}

function buildEditPayload(v: INpcFormValues): INpcEditBody {
  // Edit endpoint accepts nullable fields — empty strings become explicit
  // nulls so the row reflects the cleared state rather than retaining stale
  // text. Aliases collapse to [] which the backend treats as empty.
  return {
    name: v.name.trim(),
    aliases: v.aliases,
    role: v.role.trim() ? v.role.trim() : null,
    status: v.status,
    description: v.description.trim() ? v.description.trim() : null,
    voiceNotes: v.voiceNotes.trim() ? v.voiceNotes.trim() : null,
    appearance: v.appearance.trim() ? v.appearance.trim() : null,
  }
}

/**
 * Map NPC lifecycle status to the design-system VoxStatusPill tone.
 *   alive       → active   (lifelike arcane glow)
 *   dead        → tension  (crimson)
 *   transformed → revealed (ether/violet)
 *   fled        → archived (muted seal)
 *   unknown     → neutral
 */
function statusTone(status: TNpcStatus): "active" | "tension" | "revealed" | "archived" | "neutral" {
  switch (status) {
    case "alive":
      return "active"
    case "dead":
      return "tension"
    case "transformed":
      return "revealed"
    case "fled":
      return "archived"
    default:
      return "neutral"
  }
}

export const NPCsViewView = ({
  npcs,
  isLoading,
  initialEntityId,
  initialCreateName,
  isCreating,
  isUpdating,
  isAccepting,
  isDeleting,
  onCreate,
  onUpdate,
  onAccept,
  onDelete,
  onAcceptAllPending,
  onDeleteAllPending,
}: IViewProps): React.JSX.Element => {
  // 1. STATE
  const [bulkBusy, setBulkBusy] = useState<boolean>(false)

  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  const {
    filter,
    setFilter,
    selected: selectedNpc,
    editing,
    setEditing,
    createModalOpen,
    setCreateModalOpen,
    pendingCount,
    acceptedCount,
    visibleItems: visibleNpcs,
    pendingItems: pendingNpcs,
    selectDetail,
    closeDrawer,
  } = useWorldEntityGrid<INpc>({
    items: npcs,
    isPending: npcIsPending,
    getName: npcName,
    getId: npcId,
    initialEntityId,
  })

  const editForm = useForm<INpcFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(npcSchema),
  })

  const createForm = useForm<INpcFormValues>({
    mode: "uncontrolled",
    initialValues: emptyFormValues(),
    validate: zodResolver(npcSchema),
  })

  // 3. CALLBACKS
  const handleEditClick = useCallback((): void => {
    if (!selectedNpc) return
    editForm.setValues(formValuesFromNpc(selectedNpc))
    setEditing(true)
  }, [editForm, selectedNpc, setEditing])

  const handleEditSubmit = useCallback(
    async (values: INpcFormValues): Promise<void> => {
      if (!selectedNpc) return
      try {
        await onUpdate(selectedNpc.id, buildEditPayload(values))
        setEditing(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep edit mode open.
      }
    },
    [selectedNpc, onUpdate, setEditing],
  )

  const handleCreateSubmit = useCallback(
    async (values: INpcFormValues): Promise<void> => {
      try {
        await onCreate(buildCreatePayload(values))
        setCreateModalOpen(false)
      } catch {
        // Smart wrapper surfaces the error notification; keep modal open.
      }
    },
    [onCreate, setCreateModalOpen],
  )

  const handleAccept = useCallback((): void => {
    if (!selectedNpc) return
    void onAccept(selectedNpc.id)
  }, [selectedNpc, onAccept])

  const handleDelete = useCallback((): void => {
    if (!selectedNpc) return
    const targetId = selectedNpc.id
    modals.openConfirmModal({
      title: i18n._(msg`Delete NPC`),
      children: (
        <Text size="sm">
          <Trans>Are you sure you want to delete this NPC? This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: async () => {
        try {
          await onDelete(targetId)
          closeDrawer()
        } catch {
          // Notification handled by smart wrapper.
        }
      },
    })
  }, [selectedNpc, onDelete, i18n, closeDrawer])

  const handleOpenCreate = useCallback((): void => {
    createForm.setValues(emptyFormValues())
    setCreateModalOpen(true)
  }, [createForm, setCreateModalOpen])

  const handleAcceptAllPending = useCallback((): void => {
    if (pendingNpcs.length === 0) return
    const ids = pendingNpcs.map((n) => n.id)
    modals.openConfirmModal({
      title: i18n._(msg`Accept all pending NPCs`),
      children: (
        <Text size="sm">
          <Trans>Mark all {pendingNpcs.length} pending NPCs as accepted? You can still edit them afterwards.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Accept all`), cancel: i18n._(msg`Cancel`) },
      onConfirm: async () => {
        setBulkBusy(true)
        try {
          await onAcceptAllPending(ids)
        } finally {
          setBulkBusy(false)
        }
      },
    })
  }, [pendingNpcs, onAcceptAllPending, i18n])

  const handleDeleteAllPending = useCallback((): void => {
    if (pendingNpcs.length === 0) return
    const ids = pendingNpcs.map((n) => n.id)
    modals.openConfirmModal({
      title: i18n._(msg`Delete all pending NPCs`),
      children: (
        <Text size="sm">
          <Trans>Permanently delete all {pendingNpcs.length} pending NPCs? This cannot be undone.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete all`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: async () => {
        setBulkBusy(true)
        try {
          await onDeleteAllPending(ids)
        } finally {
          setBulkBusy(false)
        }
      },
    })
  }, [pendingNpcs, onDeleteAllPending, i18n])

  // 4. EFFECTS
  // Click-to-resolve from the chronicle (Phase WS-9): when an unresolved chip
  // navigates here with `?createName=…` set, pre-fill the create modal so the
  // DM converts the orphan mention into a curated NPC in one click.
  useEffect(() => {
    if (initialCreateName) {
      const next = emptyFormValues()
      next.name = initialCreateName
      createForm.setValues(next)
      setCreateModalOpen(true)
    }
  }, [initialCreateName, createForm, setCreateModalOpen])

  // 5. GUARDS
  if (isLoading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    )
  }

  // 6. RENDER
  return (
    <div className={shared.root}>
      <WorldEntityToolbar
        title={<Trans>NPCs</Trans>}
        acceptedCount={acceptedCount}
        pendingCount={pendingCount}
        filter={filter}
        onFilterChange={setFilter}
        bulkBusy={bulkBusy}
        onAcceptAllPending={handleAcceptAllPending}
        onDeleteAllPending={handleDeleteAllPending}
        addLabel={<Trans>Add NPC</Trans>}
        onAdd={handleOpenCreate}
      />

      <WorldEntityGrid
        items={visibleNpcs}
        emptyMessage={
          filter === "pending" ? (
            <Trans>No pending NPCs to review.</Trans>
          ) : filter === "accepted" ? (
            <Trans>No accepted NPCs yet.</Trans>
          ) : (
            <Trans>No NPCs yet. Run a session or add one manually.</Trans>
          )
        }
        renderCard={(npc) => (
          <Card
            key={npc.id}
            withBorder
            padding="md"
            data-accent="brand"
            className={clsx(shared.card, npc.pending && shared.pendingCard)}
            onClick={() => selectDetail(npc.id)}
          >
            <div className={shared.cardHeader}>
              <Text className={shared.cardName} lineClamp={1}>
                {npc.name}
              </Text>
              <Badge size="xs" color="gray" variant="light">
                {speakerInitials(npc.name)}
              </Badge>
            </div>
            {npc.role && (
              <Text fz="sm" c="dimmed" lineClamp={2}>
                {npc.role}
              </Text>
            )}
            <div className={shared.cardMeta}>
              <VoxStatusPill tone={statusTone(npc.status)}>{npc.status}</VoxStatusPill>
              {npc.pending && (
                <Badge size="xs" color="warning" variant="filled">
                  <Trans>pending</Trans>
                </Badge>
              )}
              {npc.aliases.length > 0 && (
                <Badge size="xs" color="brand" variant="outline">
                  +{npc.aliases.length} aliases
                </Badge>
              )}
            </div>
          </Card>
        )}
      />

      {/* Detail drawer */}
      <Drawer
        opened={!!selectedNpc}
        onClose={closeDrawer}
        position="right"
        size="md"
        title={selectedNpc ? selectedNpc.name : ""}
        padding="lg"
      >
        {selectedNpc && !editing && (
          <Stack gap="md">
            <Group gap="xs">
              <VoxStatusPill tone={statusTone(selectedNpc.status)}>{selectedNpc.status}</VoxStatusPill>
              {selectedNpc.pending && (
                <Badge color="warning" variant="filled">
                  <Trans>pending</Trans>
                </Badge>
              )}
            </Group>

            {selectedNpc.role && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Role</Trans>
                </span>
                <Text fz="sm">{selectedNpc.role}</Text>
              </div>
            )}

            {selectedNpc.aliases.length > 0 && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Aliases</Trans>
                </span>
                <Group gap="xs">
                  {selectedNpc.aliases.map((alias) => (
                    <Badge key={alias} variant="outline" color="brand" size="sm">
                      {alias}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {selectedNpc.description && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Description</Trans>
                </span>
                <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedNpc.description}
                </Text>
              </div>
            )}

            {selectedNpc.appearance && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Appearance</Trans>
                </span>
                <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedNpc.appearance}
                </Text>
              </div>
            )}

            {selectedNpc.voiceNotes && (
              <div className={shared.drawerSection}>
                <span className={shared.drawerSectionLabel}>
                  <Trans>Voice notes</Trans>
                </span>
                <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedNpc.voiceNotes}
                </Text>
              </div>
            )}

            <div className={shared.drawerActions}>
              <Button variant="default" onClick={handleEditClick}>
                <Trans>Edit</Trans>
              </Button>
              {selectedNpc.pending && (
                <Button color="success" onClick={handleAccept} loading={isAccepting}>
                  <Trans>Accept</Trans>
                </Button>
              )}
              <Button color="danger" variant="outline" onClick={handleDelete} loading={isDeleting}>
                <Trans>Delete</Trans>
              </Button>
            </div>
          </Stack>
        )}

        {selectedNpc && editing && (
          <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
            <Stack gap="md">
              <TextInput label={i18n._(msg`Name`)} key={editForm.key("name")} {...editForm.getInputProps("name")} />
              <TagsInput
                label={i18n._(msg`Aliases`)}
                description={i18n._(msg`Press enter after each alias`)}
                key={editForm.key("aliases")}
                {...editForm.getInputProps("aliases")}
              />
              <TextInput
                label={i18n._(msg`Role`)}
                placeholder={i18n._(msg`e.g. court wizard`)}
                key={editForm.key("role")}
                {...editForm.getInputProps("role")}
              />
              <SegmentedControl
                size="xs"
                fullWidth
                data={NPC_STATUSES.map((s) => ({ value: s, label: s }))}
                value={editForm.getValues().status}
                onChange={(v) => editForm.setFieldValue("status", v as TNpcStatus)}
              />
              <Textarea
                label={i18n._(msg`Description`)}
                autosize
                minRows={3}
                key={editForm.key("description")}
                {...editForm.getInputProps("description")}
              />
              <Textarea
                label={i18n._(msg`Appearance`)}
                autosize
                minRows={2}
                key={editForm.key("appearance")}
                {...editForm.getInputProps("appearance")}
              />
              <Textarea
                label={i18n._(msg`Voice notes`)}
                autosize
                minRows={2}
                key={editForm.key("voiceNotes")}
                {...editForm.getInputProps("voiceNotes")}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setEditing(false)}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button type="submit" loading={isUpdating}>
                  <Trans>Save</Trans>
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Drawer>

      {/* Create modal */}
      <Modal opened={createModalOpen} onClose={() => setCreateModalOpen(false)} title={i18n._(msg`Add NPC`)} size="lg">
        <form onSubmit={createForm.onSubmit(handleCreateSubmit)}>
          <Stack gap="md">
            <TextInput
              label={i18n._(msg`Name`)}
              required
              key={createForm.key("name")}
              {...createForm.getInputProps("name")}
            />
            <TagsInput
              label={i18n._(msg`Aliases`)}
              description={i18n._(msg`Press enter after each alias`)}
              key={createForm.key("aliases")}
              {...createForm.getInputProps("aliases")}
            />
            <TextInput
              label={i18n._(msg`Role`)}
              placeholder={i18n._(msg`e.g. court wizard`)}
              key={createForm.key("role")}
              {...createForm.getInputProps("role")}
            />
            <SegmentedControl
              size="xs"
              fullWidth
              data={NPC_STATUSES.map((s) => ({ value: s, label: s }))}
              value={createForm.getValues().status}
              onChange={(v) => createForm.setFieldValue("status", v as TNpcStatus)}
            />
            <Textarea
              label={i18n._(msg`Description`)}
              autosize
              minRows={3}
              key={createForm.key("description")}
              {...createForm.getInputProps("description")}
            />
            <Textarea
              label={i18n._(msg`Appearance`)}
              autosize
              minRows={2}
              key={createForm.key("appearance")}
              {...createForm.getInputProps("appearance")}
            />
            <Textarea
              label={i18n._(msg`Voice notes`)}
              autosize
              minRows={2}
              key={createForm.key("voiceNotes")}
              {...createForm.getInputProps("voiceNotes")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateModalOpen(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" loading={isCreating}>
                <Trans>Create</Trans>
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
